import { useState } from 'react';
import StatusCode from 'status-code-enum';

import { adViewApi } from 'api/adViewApi';
import { fireToast } from 'helpers/Toasts';
import { ShowPhoneNumberButton } from 'components/Toolkit/ShowPhoneNumberButton/ShowPhoneNumberButton';
import { pushToDataLayer } from 'services/gtmService';

import getConfig from 'next/config';
import { useScript } from 'hooks/UseScript';
import { getRecaptchaToken } from 'helpers/reCaptcha';
import { logBrazeRevealPhoneEvent } from 'services/brazeService';
import { useAuthOptions } from 'hooks/useAuthOptions';

const {
  publicRuntimeConfig: { GOOGLE_RECAPTCHA, INTEGRATION_TESTING },
} = getConfig();

interface RevealPhoneNumberProps {
  id: number;
  showNumberTextVariant?: boolean;
  size?: 'SMALL' | 'LARGE' | 'GROW';
  trackingId?: string;
  clickSource?: string;
  isPrimaryButton?: boolean;
  campaignId?: string | null;
  disabled?: boolean;
  sectionName?: string;
  sellerType?: 'PRIVATE' | 'TRADER' | 'DEALER';
}

function RevealPhoneNumber({
  id,
  showNumberTextVariant = false,
  size = 'SMALL',
  trackingId = 'reveal-number',
  clickSource = '',
  isPrimaryButton = false,
  campaignId,
  disabled,
  sectionName,
  sellerType,
}: RevealPhoneNumberProps) {
  useScript(
    `https://www.google.com/recaptcha/enterprise.js?render=${GOOGLE_RECAPTCHA}`,
    'recaptcha',
    [id],
  );

  const { handleLogin } = useAuthOptions();
  const [loginAndPhoneReveal, setLoginAndPhoneReveal] =
    useState<boolean>(false);

  async function handleReveal() {
    try {
      const captchaToken = await getRecaptchaToken(
        'phone_reveal',
        GOOGLE_RECAPTCHA,
        INTEGRATION_TESTING,
      );
      const { data } = await adViewApi.getPhoneNumber(
        id,
        captchaToken,
        campaignId,
      );

      pushToDataLayer({ event: 'phone_reveal', source: clickSource });
      logBrazeRevealPhoneEvent(sectionName, sellerType);

      return data.phoneNumber;
    } catch (error) {
      if (
        error.response &&
        error.response.status === StatusCode.ClientErrorUnauthorized
      ) {
        setLoginAndPhoneReveal(true);
        handleLogin();
      } else {
        fireToast({
          type: 'ERROR',
          text: 'Oops! Something went wrong, please try again later',
        });
      }
      return false;
    }
  }

  async function handleOnCall() {
    try {
      pushToDataLayer({ event: 'click_to_call', source: clickSource });
      await adViewApi.trackAdCallNumber(id, campaignId);
    } catch (_error) {}
  }

  return (
    <ShowPhoneNumberButton
      adId={id}
      onReveal={handleReveal}
      showNumberTextVariant={showNumberTextVariant}
      size={size}
      trackingId={trackingId}
      onCall={handleOnCall}
      isPrimaryButton={isPrimaryButton}
      disabled={disabled}
      setLoginAndPhoneReveal={setLoginAndPhoneReveal}
      loginAndPhoneReveal={loginAndPhoneReveal}
    />
  );
}

export { RevealPhoneNumber };
